import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { ExternalLink, Play } from "lucide-react";
import { safeHttpUrl } from "../../../Api/safeHttpUrl.js";

// The recording, seekable from the quotes.
//
// Gate 7 is "every scored parameter shows a verbatim quote AND a timestamp that
// PLAYS THE CALL AT THAT MOMENT". The timestamps were already rendered as media
// -fragment links (`...#t=47`), which is the right URL to produce and does
// nothing on its own: it opens the recording in a new tab, at the start, and
// the reader then scrubs for the moment they were promised. Half of a trust
// lever is not a trust lever - the whole argument for showing a quote is that
// the rep can check it, and a check that costs a tab and thirty seconds of
// scrubbing is one nobody performs.
//
// So the card owns a player and the quotes drive it. Clicking 0:47 seeks to
// 0:47 in place, next to the sentence it is evidence for.
//
// WHAT IS AND IS NOT PLAYABLE
// ---------------------------
// `call_link` is whatever the recorder put on the event. Out of the real
// pipeline that is the R2 object - an .mp4 or .m4a - and it plays. A
// hand-entered row may carry a link to a page instead, which cannot be embedded
// and must not be silently swallowed: those keep the external link and say
// plainly that the timestamp will open the recording rather than seek it. The
// failure mode this avoids is a play button that looks live and does nothing.

const MEDIA_EXTENSIONS = /\.(mp4|m4a|mp3|wav|ogg|webm|mov)(\?|#|$)/i;
const VIDEO_EXTENSIONS = /\.(mp4|webm|mov)(\?|#|$)/i;

export function isPlayable(link) {
  return Boolean(safeHttpUrl(link)) && MEDIA_EXTENSIONS.test(link);
}

export function stamp(seconds) {
  if (seconds == null) return null;
  const s = Math.max(0, Math.round(seconds));
  // The hour arm matters: without it a 71-minute meeting stamped its moments
  // "71:04", which no scrubber agrees with and nobody reads as 1h 11m. Long
  // discovery calls and demos routinely run past sixty minutes.
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${sec}` : `${m}:${sec}`;
}

/** The media-fragment URL, for the cases where all we can do is open the call. */
export function playHref(link, atSeconds) {
  const safe = safeHttpUrl(link);
  if (!safe) return null;
  if (atSeconds == null) return safe;
  return `${safe}${safe.includes("#") ? "&" : "#"}t=${Math.round(atSeconds)}`;
}

const muted = { color: "var(--c-500)" };

const CallPlayer = forwardRef(function CallPlayer({ link, label }, ref) {
  const mediaRef = useRef(null);
  const [cue, setCue] = useState(null);
  const playable = isPlayable(link);
  // Only an http(s) address is ever used as a link or a media source.
  const safeLink = safeHttpUrl(link);

  useImperativeHandle(ref, () => ({
    playable,
    seek(seconds) {
      const el = mediaRef.current;
      if (!el || seconds == null) return false;
      try {
        el.currentTime = Math.max(0, seconds);
        // Play rather than only seek. Somebody clicking a timestamp beside a
        // quote is asking to hear it, not to move a playhead.
        const started = el.play();
        if (started && typeof started.catch === "function") started.catch(() => {});
        setCue(seconds);
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        return true;
      } catch {
        // Autoplay policy, a media error, anything. The quote's own fallback
        // link is still on screen, so the reader is not stranded.
        return false;
      }
    },
  }), [playable]);

  if (!safeLink) {
    return (
      <p style={{ ...muted, fontSize: 11.5, margin: "8px 0 0" }}>
        No recording linked to this call, so the timestamps below are positions in the
        transcript rather than something you can play.
      </p>
    );
  }

  if (!playable) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8,
                    fontSize: 11.5, ...muted }}>
        <a href={safeLink} target="_blank" rel="noreferrer"
           style={{ display: "inline-flex", alignItems: "center", gap: 5,
                    color: "var(--blue-600)", textDecoration: "none", fontWeight: 600 }}>
          <ExternalLink size={12} /> Open the recording
        </a>
        {/* The recorder's watch page honours `#t=`, so each timestamp below
            opens the recording in a new tab already at that moment. */}
        <span>— timestamps below open it in a new tab, at that moment.</span>
      </div>
    );
  }

  const Media = VIDEO_EXTENSIONS.test(safeLink) ? "video" : "audio";
  return (
    <div style={{ marginTop: 10 }}>
      <Media
        ref={mediaRef}
        src={safeLink}
        controls
        preload="metadata"
        aria-label={label ? `Recording of ${label}` : "Call recording"}
        style={{ width: "100%", maxHeight: Media === "video" ? 320 : undefined,
                 borderRadius: 10, background: "#000", display: "block" }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4,
                    fontSize: 10.5, ...muted }}>
        <Play size={10} />
        {cue == null
          ? "Click any timestamp below to jump straight to that moment."
          : `Playing from ${stamp(cue)}.`}
      </div>
    </div>
  );
});

export default CallPlayer;
